import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button } from '../components';
import OvoContractSign from '../components/OvoContractSign';
import { Video, CalendarPlus, TrendingUp, ShieldCheck, Hourglass, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { useCockpitTitle } from '../components/EmployerCockpitLayout';
import {
  fetchEmployerProjecten,
  fetchKennismakingSlots,
  boekKennismaking,
  accordeerVoorstel,
  accordeerUitbreiding,
  wijsUitbreidingAf,
  type TrajectProject,
} from '../services/projectenApi';

/**
 * [📂 Lopende Organisatie Projecten] — het live HR-dashboard (stap 6) plus de
 * tussenstappen: kennismaking plannen (3), voorstel accorderen (4→5),
 * Model-OvO tekenen (5) en urenuitbreidingen accorderen (7).
 */

const euro = (w: string | number | null | undefined) =>
  new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(
    (typeof w === 'string' ? parseFloat(w) : w) || 0
  );

const STATUS_LABEL: Record<string, string> = {
  AANGEVRAAGD: '📢 Wacht op acceptatie door de expert',
  GEACCEPTEERD: '📅 Geaccepteerd — plan de video-kennismaking',
  PROPOSAL_SUBMITTED: '🪙 Mijlpaalvoorstel ontvangen',
  CONTRACT_PENDING: '✍️ Model-OvO wacht op ondertekening',
  ACTIEF: '📊 Live — uren-grendel actief',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
  VERLOPEN: 'Aanvraag verlopen',
  AFGEWEZEN: 'Afgewezen door de expert',
};

const SlotPicker: React.FC<{ project: TrajectProject; onGeboekt: () => void }> = ({ project, onGeboekt }) => {
  const [duur, setDuur] = React.useState<15 | 30>(30);
  const { data: slots, isLoading } = useQuery({
    queryKey: ['kennismaking-slots', project.id],
    queryFn: async () => (await fetchKennismakingSlots(project.id)).data?.data as Array<{ id: string; start_tijd: string }>,
  });

  const boekMutation = useMutation({
    mutationFn: (slotId: string) => boekKennismaking(project.id, slotId, duur),
    onSuccess: () => { toast.success('Video-kennismaking geboekt'); onGeboekt(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Boeken mislukt'),
  });

  if (isLoading) return <p className="text-sm text-gray-600">Open agenda-slots ophalen…</p>;
  if (!slots || slots.length === 0) {
    return <p className="text-sm text-gray-600">De professional heeft momenteel geen open agenda-slots.</p>;
  }

  return (
    <div className="mt-3">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-sm text-gray-700">Duur:</span>
        {[15, 30].map((d) => (
          <label key={d} className="inline-flex items-center gap-1 text-sm">
            <input type="radio" checked={duur === d} onChange={() => setDuur(d as 15 | 30)} /> {d} min
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        {slots.slice(0, 12).map((s) => (
          <Button key={s.id} size="sm" variant="outline" loading={boekMutation.isPending}
            onClick={() => boekMutation.mutate(s.id)}>
            {new Date(s.start_tijd).toLocaleString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
          </Button>
        ))}
      </div>
    </div>
  );
};

const EmployerProjectenPage: React.FC = () => {
  useCockpitTitle('Lopende Organisatie Projecten', 'Voortgang, urenbudgetten en contracten per project');
  const queryClient = useQueryClient();
  const [openSlotPicker, setOpenSlotPicker] = React.useState<string | null>(null);
  const [openContract, setOpenContract] = React.useState<string | null>(null);

  const { data: projecten, isLoading } = useQuery({
    queryKey: ['employer-projecten'],
    queryFn: async () => (await fetchEmployerProjecten()).data?.data as TrajectProject[],
  });

  const herlaad = () => queryClient.invalidateQueries({ queryKey: ['employer-projecten'] });

  const akkoordMutation = useMutation({
    mutationFn: (projectId: string) => accordeerVoorstel(projectId),
    onSuccess: () => { toast.success('Voorstel geaccordeerd — de Model-OvO is gegenereerd.'); herlaad(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Accorderen mislukt'),
  });

  const uitbreidingAkkoord = useMutation({
    mutationFn: (id: string) => accordeerUitbreiding(id),
    onSuccess: () => { toast.success('Mijlpaal verlengd — digitaal addendum aan de Model-OvO gekoppeld.'); herlaad(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Accorderen mislukt'),
  });

  const uitbreidingAfwijzen = useMutation({
    mutationFn: ({ id, reden }: { id: string; reden: string }) => wijsUitbreidingAf(id, reden),
    onSuccess: () => { toast.success('Uitbreidingsverzoek afgewezen'); herlaad(); },
    onError: (err: any) => toast.error(err.response?.data?.message || 'Afwijzen mislukt'),
  });

  if (isLoading) return <p className="text-gray-600 py-8">Projecten laden…</p>;

  return (
    <div className="max-w-4xl space-y-4">
      <div className="flex justify-end">
        <Link to="/employer/projecten/nieuw">
          <Button><Plus className="w-4 h-4 mr-1" /> Start Nieuw Organisatie Project</Button>
        </Link>
      </div>

      {(!projecten || projecten.length === 0) && (
        <Card><p className="p-8 text-center text-gray-600">Nog geen organisatieprojecten.</p></Card>
      )}

      {(projecten || []).map((p) => {
        const expertNaam = p.invoice_bedrijfsnaam || [p.first_name, p.last_name].filter(Boolean).join(' ');
        const pendingUitbreidingen = p.uitbreidingen.filter((u) => u.status === 'EXTENSION_PENDING');
        const komendeKennismaking = p.kennismakingen.find((k) => k.status === 'GEPLAND');

        return (
          <Card key={p.id}>
            <div className="p-6">
              <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                <div>
                  <span className="font-mono text-xs text-gray-500">{p.project_code}</span>
                  <h3 className="font-semibold text-gray-900">{p.titel}</h3>
                  <p className="text-sm text-gray-600">{expertNaam} · {p.professional_type}</p>
                </div>
                <span className="text-sm font-medium text-purple-800 bg-purple-50 rounded-full px-3 py-1">
                  {STATUS_LABEL[p.status] || p.status}
                </span>
              </div>

              {p.status === 'AANGEVRAAGD' && p.aanvraag_verloopt_op && (
                <p className="text-sm text-gray-600 inline-flex items-center gap-1">
                  <Hourglass className="w-4 h-4" />
                  Match vergrendeld tot {new Date(p.aanvraag_verloopt_op).toLocaleString('nl-NL')}.
                </p>
              )}

              {p.status === 'AFGEWEZEN' && p.afwijzing_reden && (
                <p className="text-sm text-red-700">Reden: {p.afwijzing_reden}</p>
              )}

              {/* Stap 3: kennismaking */}
              {(p.status === 'GEACCEPTEERD' || p.status === 'PROPOSAL_SUBMITTED') && !komendeKennismaking && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => setOpenSlotPicker(openSlotPicker === p.id ? null : p.id)}>
                    <CalendarPlus className="w-4 h-4 mr-1" /> Plan Kennismaking
                  </Button>
                  {openSlotPicker === p.id && (
                    <SlotPicker project={p} onGeboekt={() => { setOpenSlotPicker(null); herlaad(); }} />
                  )}
                </div>
              )}
              {komendeKennismaking && (
                <p className="mt-3 text-sm text-gray-700 inline-flex items-center gap-2">
                  <Video className="w-4 h-4 text-purple-600" />
                  Kennismaking {new Date(komendeKennismaking.start_tijd).toLocaleString('nl-NL')} ({komendeKennismaking.duur_minuten} min) —{' '}
                  <a className="text-purple-700 underline" href={komendeKennismaking.video_url} target="_blank" rel="noreferrer">
                    open videolink
                  </a>
                </p>
              )}

              {/* Stap 4: voorstel */}
              {p.status === 'PROPOSAL_SUBMITTED' && (
                <div className="mt-4 border-t pt-3">
                  <h4 className="font-medium text-gray-900 mb-2">Ingediend mijlpaalvoorstel</h4>
                  <ul className="text-sm text-gray-700 space-y-1 mb-3">
                    {p.milestones.map((m) => (
                      <li key={m.id}>
                        Milestone {m.volgnummer}: {m.titel} — budget {parseFloat(m.budget_uren || '0').toFixed(1)} uur ×{' '}
                        {euro(m.uurtarief)} = {euro(m.budget_bedrag)}
                      </li>
                    ))}
                  </ul>
                  <Button size="sm" loading={akkoordMutation.isPending} onClick={() => akkoordMutation.mutate(p.id)}>
                    <ShieldCheck className="w-4 h-4 mr-1" /> Akkoord — genereer Model-OvO
                  </Button>
                </div>
              )}

              {/* Stap 5: contract */}
              {p.contract && p.status !== 'ACTIEF' && (
                <div className="mt-4 border-t pt-3">
                  <Button size="sm" variant="outline" onClick={() => setOpenContract(openContract === p.id ? null : p.id)}>
                    ✍️ {openContract === p.id ? 'Verberg contract' : 'Bekijk & onderteken Model-OvO'}
                  </Button>
                  {openContract === p.id && (
                    <div className="mt-3">
                      <OvoContractSign contractId={p.contract.id} rol="werkgever" onGetekend={herlaad} />
                    </div>
                  )}
                </div>
              )}

              {/* Stap 6: live dashboard */}
              {p.status === 'ACTIEF' && (
                <div className="mt-4 border-t pt-3 space-y-3">
                  {p.milestones.map((m) => {
                    const geschreven = parseFloat(m.geschreven_uren);
                    const budgetUren = m.budget_uren ? parseFloat(m.budget_uren) : null;
                    const tarief = m.uurtarief ? parseFloat(m.uurtarief) : null;
                    const budget = parseFloat(m.budget_bedrag);
                    const benut = tarief ? Math.min((geschreven * tarief) / budget, 1) : 0;
                    return (
                      <div key={m.id}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-800">Milestone {m.volgnummer}: {m.titel}</span>
                          <span className="text-gray-600">
                            {budgetUren != null
                              ? `${geschreven.toFixed(1)} van de ${budgetUren.toFixed(0)} uur verbruikt`
                              : `${euro(geschreven * (tarief || 0))} van ${euro(budget)}`}
                          </span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                          <div className={`h-full ${benut >= 1 ? 'bg-red-500' : 'bg-purple-500'}`} style={{ width: `${Math.round(benut * 100)}%` }} />
                        </div>
                      </div>
                    );
                  })}

                  {p.contract && (
                    <button className="text-xs text-purple-700 underline" onClick={() => setOpenContract(openContract === p.id ? null : p.id)}>
                      Bekijk getekende Model-OvO
                    </button>
                  )}
                  {openContract === p.id && p.contract && (
                    <OvoContractSign contractId={p.contract.id} rol="werkgever" />
                  )}
                </div>
              )}

              {/* Stap 7: urenuitbreidingen */}
              {pendingUitbreidingen.map((u) => (
                <div key={u.id} className="mt-4 rounded-md border border-orange-300 bg-orange-50 p-4">
                  <p className="text-sm font-semibold text-orange-900 inline-flex items-center gap-1 mb-1">
                    <TrendingUp className="w-4 h-4" /> Kritiek signaal: urenuitbreiding aangevraagd
                  </p>
                  <p className="text-sm text-orange-900">
                    Milestone {u.volgnummer} ({u.milestone_titel}): +{parseFloat(u.extra_uren).toFixed(1)} uur ={' '}
                    {euro(u.extra_bedrag)} excl. btw, plus {euro(u.platform_fee_bedrag)} platformfee (15%).
                  </p>
                  <p className="text-sm text-orange-800 mt-1"><strong>Onderbouwing:</strong> {u.onderbouwing}</p>
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" loading={uitbreidingAkkoord.isPending} onClick={() => uitbreidingAkkoord.mutate(u.id)}>
                      🔐 Accordeer & Verleng Mijlpaal
                    </Button>
                    <Button size="sm" variant="danger" loading={uitbreidingAfwijzen.isPending}
                      onClick={() => {
                        const reden = window.prompt('Reden van afwijzing:');
                        if (reden && reden.trim().length >= 3) uitbreidingAfwijzen.mutate({ id: u.id, reden: reden.trim() });
                      }}>
                      Afwijzen
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
};

export default EmployerProjectenPage;
